/**
 * The retroactive name fix (docs/17 3D.4; 0073; docs/03 §5c).
 *
 * 3D.2 built the normalizer and 3D.3 put it in the form, so every name typed
 * from now on is already right. This is the other half: the 1,000+ records
 * imported before either existed.
 *
 * THE RULE THE PUNCH ROW STATES, and the whole reason this is a screen rather
 * than a script: *"Never a bare 'fix all' button over 1,000+ real records: a
 * mass mutation nobody reviewed is indistinguishable from a bug, and the audit
 * ledger would fill with changes no person chose."* So:
 *
 *  - The preview shows every proposed change as **before → after**, with enough
 *    context to recognise the record (a suite label means nothing without its
 *    property).
 *  - Rows are ticked and unticked. Nothing unticked is written, and an unticked
 *    row produces NO ledger entry — a change nobody chose must leave no trace
 *    claiming somebody did.
 *  - Apply runs through `runBulkBatch` (0071, ADR-67), so every changed record
 *    gets its own audit row carrying the batch id, and the batch carries the
 *    tally the database itself checks at commit.
 *
 * WHAT THE CLIENT MAY NOT DO IS SUPPLY THE NEW NAME. `after` travels to the
 * server, but only so the server can REFUSE a row whose recomputed value no
 * longer matches what the person reviewed. The value actually written is always
 * the one the server computes from `normalizeName` and the live vocabulary. An
 * endpoint that wrote the string it was handed would be a rename API with a
 * capitalisation-shaped name, open to anybody holding the key.
 */

import { z } from 'zod';

/**
 * The nine name surfaces, which are exactly the nine `NameField` sits on
 * (docs/03 §5c). The two lists must stay equal: a field the app fixes going
 * forward but never fixed historically is a field whose data disagrees with
 * itself forever, and one fixed here but not in the form re-earns its mess on
 * the next edit.
 */
export const NAME_SURFACES = [
  'property',
  'property_group',
  'space',
  'tenant_org',
  'tenant_contact',
  'vendor',
  'vendor_contact',
  'utility_authorized_user',
  'internal_contact',
] as const;
export type NameSurface = (typeof NAME_SURFACES)[number];

export const NAME_SURFACE_LABEL: Record<NameSurface, string> = {
  property: 'Properties',
  property_group: 'Property groups',
  space: 'Suites',
  tenant_org: 'Tenants',
  tenant_contact: 'Tenant contacts',
  vendor: 'Vendors',
  vendor_contact: 'Vendor contacts',
  utility_authorized_user: 'Utility authorized users',
  internal_contact: 'Contacts',
};

/**
 * The permission each surface's WRITE needs — the module's own edit key, not a
 * key invented for this screen. `data.normalize.execute` opens the screen; this
 * decides which groups it may offer, and a group withheld is named on screen
 * with its reason rather than silently absent (F-36).
 */
export const NAME_SURFACE_WRITE_KEY: Record<NameSurface, string> = {
  property: 'properties.edit',
  property_group: 'properties.edit',
  space: 'properties.edit',
  tenant_org: 'tenants.edit',
  tenant_contact: 'tenants.edit',
  vendor: 'vendors.edit',
  vendor_contact: 'vendors.edit',
  utility_authorized_user: 'vendors.edit',
  internal_contact: 'admin.users.manage',
};

export const nameSurfaceSchema = z.enum(NAME_SURFACES);

/** One proposed change, as the reviewer sees it. */
export interface NameFixRow {
  readonly surface: NameSurface;
  readonly id: string;
  readonly before: string;
  readonly after: string;
  /** Optimistic-concurrency handle: the row's `version` at preview time. */
  readonly version: number;
  /**
   * How a person recognises this record — the property a suite belongs to, the
   * vendor a contact works for. Null where the name IS the whole identity.
   */
  readonly context: string | null;
}

export interface NameFixGroup {
  readonly surface: NameSurface;
  readonly rows: readonly NameFixRow[];
}

/** A group the caller may not write, and why — stated, never merely omitted. */
export interface NameFixWithheld {
  readonly surface: NameSurface;
  readonly missingPermission: string;
}

export interface NameFixPreview {
  readonly groups: readonly NameFixGroup[];
  readonly withheld: readonly NameFixWithheld[];
  /** Rows across every group the caller CAN write. */
  readonly total: number;
  /**
   * True when a group hit `NAME_FIX_SCAN_LIMIT` and the preview is therefore
   * partial. Applying is still safe — it just takes another pass.
   */
  readonly truncated: boolean;
}

/**
 * The scan ceiling per surface. The real portfolio is 88 properties and 448
 * tenant orgs, so 2,000 covers it with room to grow; the cap exists so a
 * database that has grown by an order of magnitude returns a page instead of
 * timing out, and `truncated` says so out loud.
 */
export const NAME_FIX_SCAN_LIMIT = 2000;

/** The most rows one Apply may carry. A reviewer's decision, not a job queue. */
export const NAME_FIX_APPLY_LIMIT = 2000;

/**
 * A row id is the record's uuid, EXCEPT on `internal_contact`, where one record
 * carries two independently reviewable names and the id is `<uuid>#first` or
 * `<uuid>#last`. Not `z.uuid()`, therefore — which would have rejected every
 * contact row the preview offered.
 */
export const NAME_FIX_ROW_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(#(first|last))?$/;

export const nameFixApplyRowSchema = z.object({
  surface: nameSurfaceSchema,
  id: z.string().regex(NAME_FIX_ROW_ID),
  version: z.number().int().nonnegative(),
  /** What the reviewer SAW. Both halves are checked; neither is written. */
  before: z.string().min(1).max(400),
  after: z.string().min(1).max(400),
});
export type NameFixApplyRow = z.infer<typeof nameFixApplyRowSchema>;

export const nameFixApplySchema = z.object({
  rows: z.array(nameFixApplyRowSchema).min(1).max(NAME_FIX_APPLY_LIMIT),
});
export type NameFixApplyRequest = z.infer<typeof nameFixApplySchema>;

/**
 * Why a reviewed row was not applied. Every one of these is reported back and
 * none of them writes anything — including `settled`, where the answer is
 * already correct and a ledger row would claim a change that did not happen.
 */
export const NAME_FIX_CONFLICTS = [
  'changed',
  'gone',
  'settled',
  'recomputed',
  'forbidden',
] as const;
export type NameFixConflict = (typeof NAME_FIX_CONFLICTS)[number];

export const NAME_FIX_CONFLICT_LABEL: Record<NameFixConflict, string> = {
  changed: 'Somebody else edited this record after the preview',
  gone: 'The record is no longer there',
  settled: 'Already correct — nothing to change',
  recomputed: 'The capitalization rules changed after the preview',
  // Unreachable from the screen, which never offers a group the caller cannot
  // write. It exists because the ENDPOINT is the authority, and answering "no
  // longer there" to "you may not edit this" is a small lie in a report
  // somebody may act on.
  forbidden: 'Your role cannot edit this record',
};

export interface NameFixConflictRow {
  readonly surface: NameSurface;
  readonly id: string;
  readonly reason: NameFixConflict;
  /**
   * The best value we have to show the reviewer: what the record says NOW where
   * the row was read back (`changed`, `gone`), what the server recomputed
   * (`recomputed`), and the reviewed value where nothing needed reading
   * (`settled`). Null when there is nothing useful to say.
   */
  readonly current: string | null;
}

export interface NameFixResult {
  readonly applied: number;
  readonly conflicts: readonly NameFixConflictRow[];
  /** One per module touched — see the note in the service on why not one total. */
  readonly batchIds: readonly string[];
}
